import {
  addDoc,
  collection,
  doc,
  FirestoreDataConverter,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable } from 'firebase/storage';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { useMemo } from 'react';
import { auth, firestore, storage } from '@/lib/firebase';
import { BoxBrand } from '@/lib/api/boxes';

export type Apk = {
  id: string;
  name: string;
  /** Which box hardware this build is for — installs are restricted to it. */
  brand: BoxBrand;
  /** Free-text tag ("SMR-LATEST", "NV-LATEST"). Unique: assigning it to one
   *  APK clears it from any other, so a LATEST label always points at one build. */
  label?: string;
  fileName: string;
  storagePath: string;
  size: number;
  uploadDate: Date;
  uploadedBy: string;
  isDeleted?: boolean;
};

const apkConverter: FirestoreDataConverter<Apk> = {
  toFirestore: () => {
    throw new Error('use uploadApk');
  },
  fromFirestore: (snapshot) => {
    const d = snapshot.data();
    return {
      id: snapshot.id,
      name: d.name,
      brand: d.brand,
      label: d.label ?? '',
      fileName: d.fileName,
      storagePath: d.storagePath,
      size: d.size,
      uploadDate: d.uploadDate?.toDate(),
      uploadedBy: d.uploadedBy,
      isDeleted: d.isDeleted,
    };
  },
};

export function useApks() {
  const ref = useMemo(
    () => collection(firestore, 'apks').withConverter(apkConverter),
    []
  );
  const [apks, loading, error] = useCollectionData(ref, {
    snapshotListenOptions: { includeMetadataChanges: false },
  });

  const visibleApks = useMemo(
    () => apks?.filter((apk) => !apk.isDeleted),
    [apks]
  );

  return [visibleApks, loading, error] as const;
}

/** Bubble colors by label text: NEXUS blue, SMR light green, NV dark green.
 *  NEXUS is checked before NV so a label containing both reads as NEXUS.
 *  Unknown labels fall back to the plain outline badge. */
export function apkLabelClass(label: string): string {
  const l = label.toUpperCase();
  if (l.includes('NEXUS')) return 'border-transparent bg-blue-600 text-white';
  if (l.includes('SMR'))
    return 'border-transparent bg-green-400 text-green-950';
  if (l.includes('NV')) return 'border-transparent bg-green-800 text-white';
  return '';
}

/**
 * True when a labeled APK's name or file name embeds this exact version
 * string ("1.0.61" matches "dolphins_settopbox_1.0.61.apk" but not
 * "…_1.0.612.apk" or "…_11.0.61.apk"). Used to swap the Boxes table's raw
 * appVersion for the APK's label bubble.
 */
export function apkMatchesVersion(apk: Apk, version: string): boolean {
  const v = version.trim();
  if (!v) return false;
  const escaped = v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Boundaries: not preceded by a digit or "digit." (else "0.61" would match
  // inside "1.0.61"), not followed by a digit or ".digit" — while "1.0.61.apk"
  // still matches because ".a" is not a version continuation.
  const re = new RegExp(`(?<!\\d|\\d\\.)${escaped}(?!\\d|\\.\\d)`);
  return re.test(apk.name) || re.test(apk.fileName);
}

/**
 * Set or clear an APK's label. Labels are normalized to uppercase and act as
 * moving pointers: any other APK holding the same label loses it in the same
 * batch. Returns the name of the APK the label moved from, if any.
 */
export async function setApkLabel(
  id: string,
  rawLabel: string,
  apks: Apk[] | undefined
): Promise<string | null> {
  const label = rawLabel.trim().toUpperCase();
  const batch = writeBatch(firestore);
  batch.update(doc(firestore, 'apks', id), { label });
  let movedFrom: string | null = null;
  if (label) {
    for (const other of apks ?? []) {
      if (other.id !== id && (other.label ?? '').toUpperCase() === label) {
        batch.update(doc(firestore, 'apks', other.id), { label: '' });
        movedFrom = other.name;
      }
    }
  }
  await batch.commit();
  return movedFrom;
}

/** Soft delete only: the Storage object is kept so an in-flight install task
 *  can still download it. */
export function softDeleteApk(id: string) {
  return updateDoc(doc(firestore, 'apks', id), { isDeleted: true });
}

/** Uploads straight to Storage (apks/…) then records it in the `apks`
 *  collection. Unlike images this bypasses the Cloud Run API on purpose: APKs
 *  are far larger than images, and the SDK's resumable upload handles that
 *  better than a function request body would. Storage rules restrict the
 *  apks/ prefix to admin writes. */
export function uploadApk({
  file,
  name,
  brand,
  onProgress,
}: {
  file: File;
  name: string;
  brand: BoxBrand;
  onProgress?: (percent: number) => void;
}) {
  const user = auth.currentUser;
  if (!user) {
    return Promise.reject(new Error('User not authenticated'));
  }

  // Timestamp prefix keeps re-uploads of the same file distinct; sanitized so
  // the agent can pass the basename to a shell script safely.
  const safeFileName = file.name.replace(/[^\w.-]/g, '_');
  const storagePath = `apks/${Date.now()}-${safeFileName}`;
  const uploadTask = uploadBytesResumable(
    storageRef(storage, storagePath),
    file,
    { contentType: 'application/vnd.android.package-archive' }
  );

  // Resolves with the new doc id so the caller can apply a label via
  // setApkLabel() (which also sweeps the label off any other APK).
  return new Promise<string>((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) =>
        onProgress?.(
          Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
        ),
      reject,
      () => {
        addDoc(collection(firestore, 'apks'), {
          name,
          brand,
          label: '',
          fileName: file.name,
          storagePath,
          size: file.size,
          uploadDate: serverTimestamp(),
          uploadedBy: user.email ?? user.uid,
          isDeleted: false,
        }).then((ref) => resolve(ref.id), reject);
      }
    );
  });
}
